<template>
  <div
    ref="scroll"
    :class="[
      scrollbarClassName,
      observedScrollbarQuasarClass,
      `${scrollbarClassName}--${scrollbarStyle}`,
      {
        'tm-scrollbar--scroll-x': allowScrollX,
        'tm-scrollbar--stable': stable,
        'tm-scrollbar--inactive': inactive,
        'tm-scrollbar--show-scroll-y': showScrollY,
      },
    ]"
    :data-scrollbar-id="scrollbarId ?? rootScrollbarId"
  >
    <div
      ref="scrollContent"
      class="tm-scrollbar__content"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, provide, computed, ref } from '@/composition/vue/compositionApi'
import type { Scrollbars } from '@/services/browser/types'
import {
  observedScrollbarQuasarClass,
  rootScrollbarId,
  scrollbarClassName,
  type ScrollbarStyle,
  type ScrollXYPosition,
} from '@/components/shared/types'
import { useScrollProvide } from '@/composition/scroll'
import TmLogicError from '@/core/error/tmLogicError'
import type { StylesheetSizeProp } from '@/types'
import { hasHorizontalScrollbar, hasVerticalScrollbar } from '@/utils/scroll'
import { scrollBarSymbol } from '@/composition/tmScrollbar'

const props = withDefaults(
  defineProps<{
    scrollbarId?: Scrollbars
    allowScrollX?: boolean
    stable?: boolean
    centering?: 'both' | 'x' | 'y'
    scrollbarStyle?: ScrollbarStyle
    inactive?: boolean
    showScrollY?: boolean
  }>(),
  {
    scrollbarStyle: 'thumb-with-track',
  },
)

const emit = defineEmits<{
  'update:scroll-position': [ScrollXYPosition]
  'update:scrollbar-width': [number]
  'update:scrolled-to-left': [boolean]
  'update:scrolled-to-right': [boolean]
  'update:scrolled-to-top': [boolean]
  'update:scrolled-to-bottom': [boolean]
  'update:scrolled-x': [boolean]
}>()

let resizeObserver: ResizeObserver | undefined
const scroll = ref<HTMLDivElement>()
const scrollContent = ref<HTMLDivElement>()

const { emitScrollEvent } = useScrollProvide()

provide(scrollBarSymbol, scroll)

const hasVerticalScroll = ref(false)
const hasHorizontalScroll = ref(false)

const scrollbarWidth = ref<number>(0)
const scrollbarWidthStyle = computed<StylesheetSizeProp>(() => `${scrollbarWidth.value}px`)

const onScroll = () => {
  if (!scroll.value) return

  emitScrollEvent()

  const { offsetWidth, scrollWidth, scrollLeft, offsetHeight, scrollHeight, scrollTop, clientWidth } = scroll.value
  emit('update:scroll-position', {
    up: scrollTop,
    down: Math.max(scrollHeight - offsetHeight - scrollTop - 1, 0),
    left: scrollLeft,
    right: Math.max(scrollWidth - offsetWidth - scrollLeft, 0),
  })

  emit('update:scrolled-to-left', scrollLeft === 0)
  emit('update:scrolled-x', scrollLeft > 0)
  emit('update:scrolled-to-right', Math.ceil(offsetWidth + scrollLeft) >= scrollWidth)
  emit('update:scrolled-to-top', scrollTop === 0)
  emit('update:scrolled-to-bottom', Math.ceil(offsetHeight + scrollTop) >= scrollHeight)

  scrollbarWidth.value = offsetWidth - clientWidth
  emit('update:scrollbar-width', scrollbarWidth.value)
}

const onScrollCallback = () => {
  onScroll()
}

const resizeObserverCallback = (entries: ResizeObserverEntry[]) => {
  const scrollEntry = entries[0]
  if (!scrollEntry) {
    throw new TmLogicError('scrollEntry is undefined')
  }

  if (entries.length > 1) {
    throw new TmLogicError('Entries should contain only one element')
  }

  const target = scrollEntry.target

  if (!(target instanceof HTMLElement)) {
    throw new TmLogicError('"target" should be instanceof HTMLElement')
  }

  const hasVertical = hasVerticalScrollbar(target)
  const hasHorizontal = hasHorizontalScrollbar(target)
  if (
    !hasVertical &&
    hasVertical === hasVerticalScroll.value &&
    !hasHorizontal &&
    hasHorizontal === hasHorizontalScroll.value
  ) {
    return
  }
  hasVerticalScroll.value = hasVertical
  hasHorizontalScroll.value = hasHorizontal
  onScroll()
}

onMounted(() => {
  hasVerticalScroll.value = hasVerticalScrollbar(scroll.value!)
  hasHorizontalScroll.value = hasHorizontalScrollbar(scroll.value!)

  // spec: Observation will fire when observation starts if Element is being rendered, and Element’s size is not 0,0.
  resizeObserver = new ResizeObserver(resizeObserverCallback)
  resizeObserver.observe(scroll.value!)
  scroll.value!.addEventListener('scroll', onScrollCallback)

  if (props.centering === 'both') centeringByBoth()
  else if (props.centering === 'x') centeringByX()
  else if (props.centering === 'y') centeringByY()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()

  if (scroll.value) {
    scroll.value.removeEventListener('scroll', onScrollCallback)
  }
})

// methods for using from parents
const setScroll = (axes: { x?: number; y?: number }) => {
  if (!scroll.value) {
    return
  }
  if (axes.x) {
    scroll.value.scrollLeft = axes.x
  }
  if (axes.y) {
    scroll.value.scrollTop = axes.y
  }
}
const centeringByX = () => {
  const { scrollWidth, offsetWidth } = scroll.value!
  if (scrollWidth > offsetWidth) setScroll({ x: (scrollWidth - offsetWidth) / 2 })
}
const centeringByY = () => {
  const { scrollHeight, offsetHeight } = scroll.value!
  if (scrollHeight > offsetHeight) setScroll({ y: (scrollHeight - offsetHeight) / 2 })
}
const centeringByBoth = () => {
  centeringByX()
  centeringByY()
}
const scrollYToEnd = () => {
  if (!scroll.value) return
  setScroll({ y: scroll.value.scrollHeight })
}

const getScrollElement = () => {
  if (!scroll.value) {
    throw new TmLogicError('scroll.value is null')
  }
  return scroll.value
}

const getScrollContentElement = () => {
  return scrollContent.value
}

defineExpose({
  scrollYToEnd,
  getScrollElement,
  getScrollContentElement,
  centeringByX,
  centeringByY,
  centeringByBoth,
  scroll,
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.tm-scrollbar {
  --tm-scrollbar-width: v-bind(scrollbarWidthStyle);

  @include scrollbar-variants();
  display: flex;
  flex-flow: column;
  flex: 1;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;

  &--show-scroll-y {
    overflow-y: scroll;
  }

  &--inactive {
    overflow: unset;
  }

  &--stable {
    scrollbar-gutter: stable;
  }

  &--scroll-x {
    overflow-x: auto;
  }
  &__content {
    display: flex;
    flex-flow: column;
    flex: 1;
  }
}
</style>
